import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { listMyDeliveries } from "@/lib/partner-portal.functions";
import { WEBHOOK_EVENTS } from "@/lib/partner-scopes";
import { SiteHeader } from "@/components/site/SiteHeader";
import { PartnerNav, deliveryLabel } from "@/components/partner/PartnerNav";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/partner/webhooks_/deliveries")({
  head: () => ({
    meta: [
      { title: "Entregas de webhooks — Portal de socios" },
      { name: "description", content: "Historial de entregas de eventos con estado, código HTTP, intentos y respuesta." },
      { property: "og:title", content: "Entregas de webhooks — Portal de socios" },
      { property: "og:description", content: "Auditoría de entregas de eventos para socios de Mostrando México." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DeliveriesPage,
});

type Status = "" | "pending" | "success" | "failed" | "dead";

function DeliveriesPage() {
  const [eventType, setEventType] = useState("");
  const [status, setStatus] = useState<Status>("");

  const { data, isLoading } = useQuery({
    queryKey: ["partner", "deliveries", eventType, status],
    queryFn: () =>
      listMyDeliveries({
        data: {
          limit: 100,
          ...(eventType ? { event_type: eventType } : {}),
          ...(status ? { status: status as Exclude<Status, ""> } : {}),
        },
      }),
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-semibold">Entregas de webhooks</h1>
        <PartnerNav />

        <div className="flex flex-wrap gap-3">
          <select
            aria-label="Filtrar por evento"
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
          >
            <option value="">Todos los eventos</option>
            {WEBHOOK_EVENTS.map((ev) => (
              <option key={ev} value={ev}>
                {ev}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar por estado"
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value as Status)}
          >
            <option value="">Todos los estados</option>
            <option value="success">delivered</option>
            <option value="pending">pending</option>
            <option value="failed">retrying / failed</option>
            <option value="dead">failed (agotado)</option>
          </select>
        </div>

        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin entregas para este filtro.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border bg-card">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-3">Evento</th>
                  <th className="p-3">Endpoint</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3">HTTP</th>
                  <th className="p-3">Intentos</th>
                  <th className="p-3">Duración</th>
                  <th className="p-3">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((d: any) => (
                  <tr key={d.id} className="border-t align-top">
                    <td className="p-3 font-mono text-xs">
                      {d.event_type}
                      <div className="text-[10px] text-muted-foreground">{d.event_id}</div>
                    </td>
                    <td className="max-w-[220px] break-all p-3 font-mono text-[11px]">{d.endpoint ?? "—"}</td>
                    <td className="p-3">
                      {deliveryLabel(d.status, d.next_retry_at)}
                      {d.response_body && (
                        <div className="max-w-[220px] break-all text-[10px] text-muted-foreground">{d.response_body}</div>
                      )}
                    </td>
                    <td className="p-3">{d.response_status ?? "—"}</td>
                    <td className="p-3">{d.attempts}</td>
                    <td className="p-3">{d.duration_ms != null ? `${d.duration_ms} ms` : "—"}</td>
                    <td className="p-3 text-xs">{new Date(d.created_at).toLocaleString("es-MX")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
